import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { addDays, endOfDay } from 'date-fns';

import { DEFAULT_TIME_ZONE_NAME } from '@shared/constants';
import media from '../../utils/mediaQueries';
import Loader from '../loader';
import stores from '../../stores';

import iconClipboard from '../../images/icon-clipboard-notes.svg';

import { FONT_FAMILY_MAIN, TEXT_COLOR, NOTES_COLOR } from '../../theme';
import { MEETING_DEFAULT_KIND } from './constants';
import { FormStep } from './FormStep';
import { CalendarStep } from './CalendarStep';
import { MessageStep } from './MessageStep';

const STEPS = [
  'renderCalendar',
  'renderForm',
  'renderMessage',
];

const MAX_STEP_INDEX = STEPS.length - 1;

const Container = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  height: 100%;
`;

const Title = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0.2px;
  color: ${TEXT_COLOR};
  margin-bottom: 16px;

  ${media.mobile`
    font-size: 18px;
    line-height: 1.44;
  `}
`;

const MeetingsCount = styled.div`
  font-family: ${FONT_FAMILY_MAIN};
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: -0.1px;
  color: ${NOTES_COLOR};
  border-radius: 8px;
  border: solid 1px #f4f7f9;
  margin-bottom: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 30px;
  padding-right: 10px;
  position: relative;

  &::before {
    content: '';
    width: 9px;
    height: 12px;
    background-image: url(${iconClipboard});
    background-size: contain;
    background-repeat: no-repeat;
    position: absolute;
    left: 12px;
  }
`;

const MESSAGE_SHOW_TIME = 30000;
const MESSAGE_STEP_NUMBER = 2;

class SchoolMeetingsClassComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentDate: null,
      currentTime: null,
      currentStep: 0,
      slideIndex: 0,
      cachedUser: null,
      isFormSubmitting: false,
      kind: MEETING_DEFAULT_KIND,
    };
  }

  componentDidMount = () => {
    const { schoolId } = this.props;
    const today = new Date();
    const minDate = today.toISOString();
    const maxDate = endOfDay(addDays(today, 40)).toISOString();
    const callback = (date) => {
      this.setState({
        currentDate: date,
      });
    };

    const { meetingStore } = stores;
    meetingStore.getMeetingSessions(schoolId, minDate, maxDate, callback);
    if (!(meetingStore.meetingSubjects.length || meetingStore.isMeetingSubjectsLoading)) {
      meetingStore.getMeetingSubjects(schoolId);
    }
    meetingStore.getMeetingsCount(schoolId);
  };

  onHeaderSlotClick = (date) => {
    this.setState({
      currentDate: date,
    });
  };

  onSlotClick = (time) => {
    this.setState({
      currentTime: time,
    });
  };

  onSlideChanged = (slideIndex) => {
    this.setState({
      slideIndex,
    });
  };

  cachUser = (data) => {
    this.setState({
      cachedUser: data,
    });
  };

  toNextStep = () => {
    const { currentStep } = this.state;
    const nextStep = Math.min(currentStep + 1, MAX_STEP_INDEX);

    this.setState({
      currentStep: nextStep,
    });

    if (nextStep === MESSAGE_STEP_NUMBER) {
      const timing = setTimeout(() => {
        this.setState({
          currentStep: 0,
          currentTime: null,
        });
        clearTimeout(timing);
      }, MESSAGE_SHOW_TIME);
    }
  };

  toBackStep = () => {
    const { currentStep } = this.state;

    this.setState({
      currentStep: currentStep - 1,
    });
  };

  onFormSubmit = async (data, formikBag) => {
    const { schoolId } = this.props;
    const { currentTime } = this.state;
    const formattedData = {
      school_id: schoolId,
      scheduled_at: currentTime,
      first_name: data.firstname,
      last_name: data.lastname,
      email: data.email,
      phone: data.phone,
      category: data.category,
      new_google_recaptcha_token: data.new_google_recaptcha_token,
      kind: data.kind,
      message: data.message,
    };
    this.setState({ kind: data.kind });
    this.cachUser(data);

    try {
      this.setState({ isFormSubmitting: true });
      await stores.meetingStore.bookMeeting(formattedData, formikBag);
      await stores.theoryAccessStore.checkRemainingAccess(schoolId);
      this.toNextStep();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      this.setState({ isFormSubmitting: false });
    }
  };

  renderCalendar = () => {
    const { schoolTimeZone } = this.props;
    const { currentDate, currentTime, slideIndex } = this.state;
    const { days, meetingSlots } = stores.meetingStore;

    return (
      <CalendarStep
        currentDate={currentDate}
        toNextStep={this.toNextStep}
        onHeaderSlotClick={this.onHeaderSlotClick}
        meetingSlots={meetingSlots}
        onSlotClick={this.onSlotClick}
        onSlideChanged={this.onSlideChanged}
        slideIndex={slideIndex}
        days={days}
        currentTime={currentTime}
        timeZone={schoolTimeZone}
      />
    );
  };

  renderForm = () => {
    const { schoolTimeZone, schoolBookableMeetingKinds, isMessageAllowed } = this.props;
    const { currentTime, cachedUser, isFormSubmitting } = this.state;
    const { meetingSubjects } = stores.meetingStore;

    return (
      <FormStep
        cachedUser={cachedUser}
        toBackStep={this.toBackStep}
        onFormSubmit={this.onFormSubmit}
        meetingSubjects={meetingSubjects}
        isFormSubmitting={isFormSubmitting}
        currentTime={currentTime}
        timeZone={schoolTimeZone}
        bookableMeetingKinds={schoolBookableMeetingKinds}
        isMessageAllowed={isMessageAllowed}
      />
    );
  };

  renderMessage = () => {
    const { currentTime, kind } = this.state;
    const { hasRemainingAccesses } = stores.theoryAccessStore;
    const { isAlreadyBooked } = stores.meetingStore;
    const { schoolName, schoolPhone, schoolTimeZone } = this.props;

    return (
      <MessageStep
        schoolPhone={schoolPhone}
        schoolName={schoolName}
        hasRemainingAccesses={hasRemainingAccesses}
        isAlreadyBooked={isAlreadyBooked}
        kind={kind}
        currentTime={currentTime}
        timeZone={schoolTimeZone}
      />
    );
  };

  renderContent = (step) => {
    const content = STEPS[step];

    return this[content]();
  };

  render() {
    const { isBusy, meetingsCount, isMeetingsCountLoading } = stores.meetingStore;
    const { currentStep } = this.state;

    return (
      <Container>
        <Title>Prendre rendez-vous</Title>
        {!isMeetingsCountLoading && meetingsCount
          ? (
            <MeetingsCount>
              {meetingsCount}
              {' '}
              élèves ont déjà pris rendez-vous avec cette auto-école.
            </MeetingsCount>
          ) : null}
        {
          isBusy
            ? (<Loader containerHeight={200} />)
            : this.renderContent(currentStep)
        }
      </Container>
    );
  }
}

SchoolMeetingsClassComponent.propTypes = {
  schoolId: PropTypes.string,
  schoolName: PropTypes.string,
  schoolPhone: PropTypes.string,
  schoolTimeZone: PropTypes.string,
  schoolBookableMeetingKinds: PropTypes.array,
  isMessageAllowed: PropTypes.bool,
};

SchoolMeetingsClassComponent.defaultProps = {
  schoolId: '',
  schoolName: '',
  schoolPhone: '',
  schoolTimeZone: DEFAULT_TIME_ZONE_NAME,
  schoolBookableMeetingKinds: [],
  isMessageAllowed: false,
};

const SchoolMeetings = observer(SchoolMeetingsClassComponent);

export { SchoolMeetings };
